// AGC: StorageRulesAdmin — Admin panel for configuring which inventory categories
// AGC: require container storage (e.g., fuel → tank, grains → silo).
// AGC: When enabled, SOTA and WhatsApp flows will route items to containers
// AGC: instead of creating standalone inventory items.

import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useTranslation } from '../../hooks/useTranslation'
import {
  Save,
  Loader2,
  RefreshCw,
  AlertCircle,
  Check,
  X,
  Fuel,
  Warehouse,
  ToggleLeft,
  ToggleRight,
  Info,
} from 'lucide-react'
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore'

// AGC: Rule type for a category → container mapping
interface ContainerRule {
  id: string
  inventoryCategory: string
  containerType: 'tank' | 'silo'
  label: string
  description: string
  enabled: boolean
  defaultCapacity: number
  defaultUnit: string
  fuelTypes?: string[]
  icon: string
}

// AGC: Default rules — these are the initial configuration
const DEFAULT_RULES: ContainerRule[] = [
  {
    id: 'fuel_tank',
    inventoryCategory: 'fuel',
    containerType: 'tank',
    label: 'Combustível → Tanque',
    description: 'Diesel, gasolina, etanol e outros combustíveis devem ser armazenados em tanques. Compras de combustível via voz/WhatsApp serão automaticamente direcionadas para um tanque existente ou criarão um novo.',
    enabled: true,
    defaultCapacity: 10000,
    defaultUnit: 'L',
    fuelTypes: ['diesel', 'gasolina', 'etanol', 'arla', 'querosene', 'biodiesel'],
    icon: '⛽',
  },
  {
    id: 'grain_silo',
    inventoryCategory: 'seed',
    containerType: 'silo',
    label: 'Grãos/Sementes → Silo',
    description: 'Grãos e sementes podem ser armazenados em silos. Quando habilitado, entradas de grãos serão direcionadas para silos disponíveis.',
    enabled: false,
    defaultCapacity: 50000,
    defaultUnit: 'kg',
    icon: '🌾',
  },
  {
    id: 'fertilizer_tank',
    inventoryCategory: 'fertilizer',
    containerType: 'tank',
    label: 'Fertilizante Líquido → Tanque',
    description: 'Fertilizantes líquidos podem ser armazenados em tanques. Fertilizantes sólidos continuam como estoque normal.',
    enabled: false,
    defaultCapacity: 5000,
    defaultUnit: 'L',
    icon: '🧪',
  },
]

export const StorageRulesAdmin: React.FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()

  const [rules, setRules] = useState<ContainerRule[]>(DEFAULT_RULES)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // AGC: Load rules from Firestore
  const loadRules = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const db = getFirestore()
      const docRef = doc(db, 'voiceConfig', 'containerRules')
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        const data = docSnap.data()
        if (data.rules && Array.isArray(data.rules)) {
          setRules(data.rules)
          console.log('✅ AGC: Loaded container rules from Firebase')
        }
      } else {
        console.log('📝 AGC: No container rules found, using defaults')
        setRules(DEFAULT_RULES)
      }
    } catch (err) {
      console.error('❌ AGC: Error loading container rules:', err)
      setError('Erro ao carregar regras de armazenamento')
    } finally {
      setIsLoading(false)
    }
  }, [])

  // AGC: Save rules to Firestore
  const saveRules = useCallback(async () => {
    setIsSaving(true)
    setError(null)

    try {
      const db = getFirestore()
      const docRef = doc(db, 'voiceConfig', 'containerRules')

      await setDoc(docRef, {
        rules,
        updatedAt: new Date().toISOString(),
        updatedBy: user?.email || 'unknown',
      })

      setSuccessMessage('Regras salvas com sucesso! Reinicie o backend para aplicar.')
      setTimeout(() => setSuccessMessage(null), 5000)
      console.log('✅ AGC: Container rules saved to Firebase')
    } catch (err) {
      console.error('❌ AGC: Error saving container rules:', err)
      setError('Erro ao salvar regras')
    } finally {
      setIsSaving(false)
    }
  }, [rules, user])

  useEffect(() => {
    loadRules()
  }, [loadRules])

  // AGC: Toggle a rule's enabled state
  const toggleRule = (ruleId: string) => {
    setRules(prev =>
      prev.map(r => r.id === ruleId ? { ...r, enabled: !r.enabled } : r)
    )
  }

  // AGC: Update a rule's default capacity
  const updateCapacity = (ruleId: string, value: string) => {
    const num = parseFloat(value.replace(',', '.'))
    if (isNaN(num) || num <= 0) return
    setRules(prev =>
      prev.map(r => r.id === ruleId ? { ...r, defaultCapacity: num } : r)
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-green-500" />
        <span className="ml-3 text-slate-600 dark:text-slate-300">Carregando regras...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* AGC: Header */}
      <div className="bg-white dark:bg-admin-card-dark rounded-lg shadow-card dark:shadow-none p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Warehouse className="w-8 h-8 text-orange-500" />
            <div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                Regras de Armazenamento
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Configure quais categorias de estoque devem ser armazenadas em containers (tanques/silos)
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={loadRules}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Recarregar
            </button>

            <button
              onClick={saveRules}
              disabled={isSaving}
              className="flex items-center gap-2 px-6 py-2 bg-brand-primary text-white dark:bg-brand-light dark:text-[#052e22] rounded-lg hover:bg-brand-dark dark:hover:opacity-90 disabled:opacity-50 transition-colors"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Salvar Alterações
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-error-50 dark:bg-red-500/15 text-error-700 dark:text-red-400 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
          </div>
        )}

        {successMessage && (
          <div className="mt-4 p-3 bg-success-50 dark:bg-emerald-500/15 text-brand-primary dark:text-emerald-400 rounded-lg flex items-center gap-2">
            <Check className="w-5 h-5" />
            {successMessage}
          </div>
        )}
      </div>

      {/* AGC: Rules Cards */}
      <div className="space-y-4">
        {rules.map(rule => (
          <div
            key={rule.id}
            className={`bg-white dark:bg-admin-card-dark rounded-lg shadow-card dark:shadow-none overflow-hidden border-l-4 transition-colors ${
              rule.enabled ? 'border-green-500' : 'border-slate-200 dark:border-white/10'
            }`}
          >
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <span className="text-3xl">{rule.icon}</span>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                      {rule.label}
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        rule.enabled
                          ? 'bg-success-50 dark:bg-emerald-500/15 text-brand-primary dark:text-emerald-400'
                          : 'bg-slate-100 dark:bg-white/[0.04] text-slate-500 dark:text-slate-400'
                      }`}>
                        {rule.enabled ? 'ATIVO' : 'INATIVO'}
                      </span>
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-300 mt-1 max-w-2xl">{rule.description}</p>
                  </div>
                </div>

                <button
                  onClick={() => toggleRule(rule.id)}
                  className="flex-shrink-0"
                  title={rule.enabled ? 'Desativar' : 'Ativar'}
                >
                  {rule.enabled ? (
                    <ToggleRight className="w-10 h-10 text-green-500" />
                  ) : (
                    <ToggleLeft className="w-10 h-10 text-gray-400" />
                  )}
                </button>
              </div>

              {/* AGC: Rule configuration */}
              {rule.enabled && (
                <div className="mt-4 pt-4 border-t border-slate-100 dark:border-white/5 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Tipo de Container
                    </label>
                    <div className="px-3 py-2 bg-slate-50 dark:bg-white/[0.02] rounded-lg text-sm text-slate-800 dark:text-slate-200 font-medium">
                      {rule.containerType === 'tank' ? '🛢️ Tanque (líquidos)' : '🏗️ Silo (sólidos)'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Capacidade Padrão (novos containers)
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={rule.defaultCapacity}
                        onChange={(e) => updateCapacity(rule.id, e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-primary dark:focus:ring-brand-light"
                        min="1"
                      />
                      <span className="text-sm text-slate-500 dark:text-slate-400 font-medium">{rule.defaultUnit}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Categoria de Estoque
                    </label>
                    <div className="px-3 py-2 bg-slate-50 dark:bg-white/[0.02] rounded-lg text-sm text-slate-600 dark:text-slate-300">
                      {rule.inventoryCategory}
                    </div>
                  </div>

                  {rule.fuelTypes && (
                    <div className="md:col-span-3">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        Tipos Reconhecidos (via voz/WhatsApp)
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {rule.fuelTypes.map(ft => (
                          <span key={ft} className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">
                            {ft}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* AGC: How it works */}
      <div className="bg-info-50 dark:bg-blue-500/15 border border-slate-200 dark:border-white/5 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-info-700 dark:text-blue-400 mb-3 flex items-center gap-2">
          <Info className="w-5 h-5" />
          Como funciona
        </h3>
        <ul className="space-y-2 text-sm text-info-700 dark:text-blue-400">
          <li>
            <strong>Combustível → Tanque:</strong> Quando habilitado, toda compra de combustível via WhatsApp ou voz será
            direcionada para um tanque existente. Se não houver tanque, o sistema pergunta se deseja criar um.
          </li>
          <li>
            <strong>Grãos → Silo:</strong> Quando habilitado, entradas de grãos e sementes serão direcionadas para silos.
            Ideal para controle de armazenagem por silo com capacidade.
          </li>
          <li>
            <strong>SOTA Integration:</strong> O sistema de voz (SOTA) reconhece automaticamente referências a tanques e silos
            nos comandos de voz, como "encher tanque 1 com 5000 litros de diesel".
          </li>
          <li>
            <strong>Capacidade Padrão:</strong> Quando um novo container precisa ser criado automaticamente, esta é a capacidade
            sugerida ao usuário via WhatsApp. O usuário pode alterar antes de confirmar.
          </li>
          <li>
            Clique em <strong>Salvar Alterações</strong> para persistir no Firebase. As mudanças são aplicadas imediatamente
            para novos comandos de voz e WhatsApp.
          </li>
        </ul>
      </div>
    </div>
  )
}

export default StorageRulesAdmin
